import axios from 'axios';
import { logger as defaultLogger } from '../logger.js';
export class LimsApiClient {
    constructor(config) {
        this.config = config;
        this.log = config.logger ?? defaultLogger;
        this.axiosInstance = axios.create({
            baseURL: config.baseUrl,
            timeout: config.timeout || 30000,
            headers: {
                'Content-Type': 'application/json',
                ...(config.authToken && { Authorization: `Bearer ${config.authToken}` }),
            },
        });
        this.axiosInstance.interceptors.request.use((reqConfig) => {
            if (this.config.suppressEvents) {
                const method = (reqConfig.method ?? '').toUpperCase();
                if (method === 'POST' || method === 'PATCH' || method === 'DELETE') {
                    reqConfig.headers = reqConfig.headers ?? {};
                    reqConfig.headers['X-LIMS-Suppress-Events'] = 'true';
                }
            }
            this.log.debug({ url: reqConfig.url, method: reqConfig.method }, 'LIMS API request');
            return reqConfig;
        }, (error) => {
            this.log.error({ error }, 'LIMS API request error');
            return Promise.reject(error);
        });
        this.axiosInstance.interceptors.response.use((response) => response, (error) => {
            if (error.response) {
                this.log.error({
                    status: error.response.status,
                    statusText: error.response.statusText,
                    url: error.config?.url,
                    data: error.response.data,
                }, 'LIMS API response error');
            }
            else {
                this.log.error({ error: error.message }, 'LIMS API network error');
            }
            return Promise.reject(error);
        });
    }
    async request(config) {
        try {
            const response = await this.axiosInstance.request(config);
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                const message = error.response?.data?.error || error.message || 'Unknown error';
                throw new Error(`LIMS API error: ${message}`);
            }
            throw error;
        }
    }
    /**
     * Return a new client that either emits or suppresses entity events
     * on the server for mutation requests (POST/PATCH/DELETE).
     */
    withEmitEvents(enabled) {
        return new LimsApiClient({ ...this.config, suppressEvents: !enabled });
    }
    buildQueryString(options) {
        const pairs = [];
        if (options.limit !== undefined)
            pairs.push(`limit=${encodeURIComponent(String(options.limit))}`);
        if (options.offset !== undefined)
            pairs.push(`offset=${encodeURIComponent(String(options.offset))}`);
        if (options.filters && options.filters.length > 0) {
            pairs.push(`filters=${encodeURIComponent(JSON.stringify(options.filters))}`);
        }
        if (options.sort_by)
            pairs.push(`sort_by=${encodeURIComponent(options.sort_by)}`);
        if (options.sort_order)
            pairs.push(`sort_order=${encodeURIComponent(options.sort_order)}`);
        if (options.exclude_attributes && options.exclude_attributes.length > 0) {
            pairs.push(`exclude_attributes=${encodeURIComponent(options.exclude_attributes.join(','))}`);
        }
        if (options.search) {
            pairs.push(`search=${encodeURIComponent(options.search)}`);
        }
        if (options.search_columns && options.search_columns.length > 0) {
            pairs.push(`search_columns=${encodeURIComponent(JSON.stringify(options.search_columns))}`);
        }
        const queryString = pairs.join('&');
        return queryString ? `?${queryString}` : '';
    }
    async getEntity(entityType, id, options) {
        if (typeof id === 'number') {
            return this.request({
                method: 'GET',
                url: `/api/v0/lims/${entityType}/${id}`,
            });
        }
        const queryString = this.buildQueryString(options || {});
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/${entityType}${queryString}`,
        });
    }
    async forEntity(entityType, options, callback) {
        const limit = options.limit ?? 100;
        let offset = options.offset ?? 0;
        while (true) {
            const page = (await this.getEntity(entityType, undefined, {
                ...options,
                limit,
                offset,
            }));
            for (const entity of page.data) {
                await callback(entity);
            }
            if (!page.pagination.hasMore || page.data.length === 0)
                break;
            offset += page.data.length;
        }
    }
    async createEntity(entityType, entities) {
        return this.request({
            method: 'POST',
            url: `/api/v0/lims/${entityType}`,
            data: { entities },
        });
    }
    async updateEntity(entityType, entities) {
        return this.request({
            method: 'PATCH',
            url: `/api/v0/lims/${entityType}`,
            data: { entities },
        });
    }
    async deleteEntity(entityType, id) {
        return this.request({
            method: 'DELETE',
            url: `/api/v0/lims/${entityType}/${id}`,
        });
    }
    async getSamples(idOrOptions) {
        if (typeof idOrOptions === 'number') {
            return this.getEntity('samples', idOrOptions);
        }
        return this.getEntity('samples', undefined, idOrOptions);
    }
    async createSamples(entities) {
        return this.createEntity('samples', entities);
    }
    async updateSamples(entities) {
        return this.updateEntity('samples', entities);
    }
    async deleteSample(id) {
        return this.deleteEntity('samples', id);
    }
    async getTests(idOrOptions) {
        if (typeof idOrOptions === 'number') {
            return this.getEntity('tests', idOrOptions);
        }
        return this.getEntity('tests', undefined, idOrOptions);
    }
    async createTests(entities) {
        return this.createEntity('tests', entities);
    }
    async updateTests(entities) {
        return this.updateEntity('tests', entities);
    }
    async deleteTest(id) {
        return this.deleteEntity('tests', id);
    }
    async getAssays(idOrOptions) {
        if (typeof idOrOptions === 'number') {
            return this.getEntity('assays', idOrOptions);
        }
        return this.getEntity('assays', undefined, idOrOptions);
    }
    async createAssays(entities) {
        return this.createEntity('assays', entities);
    }
    async updateAssays(entities) {
        return this.updateEntity('assays', entities);
    }
    async deleteAssay(id) {
        return this.deleteEntity('assays', id);
    }
    async getOrders(idOrOptions) {
        if (typeof idOrOptions === 'number') {
            return this.getEntity('orders', idOrOptions);
        }
        return this.getEntity('orders', undefined, idOrOptions);
    }
    async createOrders(entities) {
        return this.createEntity('orders', entities);
    }
    async updateOrders(entities) {
        return this.updateEntity('orders', entities);
    }
    async deleteOrder(id) {
        return this.deleteEntity('orders', id);
    }
    async getEntityByType(entityType, id) {
        const plural = LimsApiClient.ENTITY_TYPE_TO_PLURAL[entityType];
        if (!plural)
            throw new Error(`Unknown entity type: ${entityType}`);
        return this.getEntity(plural, id);
    }
    async listEntitiesByType(entityType, options) {
        const plural = LimsApiClient.ENTITY_TYPE_TO_PLURAL[entityType];
        if (!plural)
            throw new Error(`Unknown entity type: ${entityType}`);
        return this.getEntity(plural, undefined, options);
    }
    async getAttachments(idOrOptions) {
        if (typeof idOrOptions === 'number') {
            return this.getEntity('attachments', idOrOptions);
        }
        return this.getEntity('attachments', undefined, idOrOptions);
    }
    async createAttachments(entities) {
        return this.createEntity('attachments', entities);
    }
    async updateAttachments(entities) {
        return this.updateEntity('attachments', entities);
    }
    async deleteAttachment(id) {
        return this.deleteEntity('attachments', id);
    }
    // ── Auto-paginating iterators ──────────────────────────────────────
    async forSamples(options, callback) {
        return this.forEntity('samples', options, callback);
    }
    async forTests(options, callback) {
        return this.forEntity('tests', options, callback);
    }
    async forAssays(options, callback) {
        return this.forEntity('assays', options, callback);
    }
    async forOrders(options, callback) {
        return this.forEntity('orders', options, callback);
    }
    async forAttachments(options, callback) {
        return this.forEntity('attachments', options, callback);
    }
    async getEntitiesReferencing(entityType, entityId, relationshipType = 'references') {
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/relationships/referencing/${entityType}/${entityId}?relationship_type=${encodeURIComponent(relationshipType)}`,
        });
    }
    async getReferencedEntities(entityType, entityId) {
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/relationships/referenced/${entityType}/${entityId}`,
        });
    }
    async getAttributes(entityType) {
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/attributes/${entityType}`,
        });
    }
    async getAttributeValues(entityType, attributeName, search, limit) {
        const params = new URLSearchParams();
        if (search)
            params.set('search', search);
        if (limit != null)
            params.set('limit', String(limit));
        const qs = params.toString();
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/attributes/${entityType}/${encodeURIComponent(attributeName)}/values${qs ? `?${qs}` : ''}`,
        });
    }
    async upsertAttribute(attribute) {
        return this.request({
            method: 'POST',
            url: '/api/v0/lims/attributes',
            data: attribute,
        });
    }
    async deleteAttribute(entityType, name) {
        return this.request({
            method: 'DELETE',
            url: `/api/v0/lims/attributes/${entityType}/${encodeURIComponent(name)}`,
        });
    }
    async getEvents(filters) {
        const params = new URLSearchParams();
        if (filters?.entityType)
            params.set('entity_type', filters.entityType);
        if (filters?.entityId != null)
            params.set('entity_id', String(filters.entityId));
        if (filters?.action)
            params.set('action', filters.action);
        if (filters?.listenerName)
            params.set('listener_name', filters.listenerName);
        if (filters?.status)
            params.set('status', filters.status);
        if (filters?.since)
            params.set('since', filters.since);
        if (filters?.until)
            params.set('until', filters.until);
        if (filters?.labId !== undefined)
            params.set('lab_id', filters.labId === null ? 'null' : String(filters.labId));
        if (filters?.limit != null)
            params.set('limit', String(filters.limit));
        if (filters?.offset != null)
            params.set('offset', String(filters.offset));
        const qs = params.toString();
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/events${qs ? `?${qs}` : ''}`,
        });
    }
    async healthCheck() {
        return this.request({
            method: 'GET',
            url: '/health',
        });
    }
    async getEntityHistory(entityType, entityId) {
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/history/${entityType}/${entityId}`,
        });
    }
    async listParsers() {
        return this.request({
            method: 'GET',
            url: '/api/v0/lims/parsers',
        });
    }
    async uploadResults(req) {
        return this.request({
            method: 'POST',
            url: '/api/v0/lims/upload-results',
            data: {
                parser: req.parser,
                fileName: req.fileName,
                fileData: Buffer.from(req.fileData).toString('base64'),
                dryRun: req.dryRun,
            },
        });
    }
    async getParserEvents(filters) {
        const params = new URLSearchParams();
        if (filters?.parserName)
            params.set('parser_name', filters.parserName);
        if (filters?.status)
            params.set('status', filters.status);
        if (filters?.testId != null)
            params.set('test_id', String(filters.testId));
        if (filters?.since)
            params.set('since', filters.since);
        if (filters?.until)
            params.set('until', filters.until);
        if (filters?.labId !== undefined)
            params.set('lab_id', filters.labId === null ? 'null' : String(filters.labId));
        if (filters?.limit != null)
            params.set('limit', String(filters.limit));
        if (filters?.offset != null)
            params.set('offset', String(filters.offset));
        const qs = params.toString();
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/parser-events${qs ? `?${qs}` : ''}`,
        });
    }
    async getParserEventFile(id) {
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/parser-events/${id}/file`,
        });
    }
    async rerunParserEvent(id) {
        return this.request({
            method: 'POST',
            url: `/api/v0/lims/parser-events/${id}/rerun`,
        });
    }
    // ── User management ──────────────────────────────────────────────────
    async syncUser(email, name) {
        return this.request({
            method: 'POST',
            url: '/api/v0/lims/users/sync',
            data: { email, name },
        });
    }
    async getCurrentUser(email) {
        return this.request({
            method: 'GET',
            url: `/api/v0/lims/users/me?email=${encodeURIComponent(email)}`,
        });
    }
    async listUsers() {
        return this.request({
            method: 'GET',
            url: '/api/v0/lims/users',
        });
    }
    async setUserAdmin(email, isAdmin) {
        return this.request({
            method: 'PATCH',
            url: `/api/v0/lims/users/${encodeURIComponent(email)}/admin`,
            data: { isAdmin },
        });
    }
    async getEntityCount(entityType, filters) {
        const qs = filters?.length ? `?filters=${encodeURIComponent(JSON.stringify(filters))}` : '';
        const result = await this.request({
            method: 'GET',
            url: `/api/v0/lims/${entityType}/count${qs}`,
        });
        return result.count;
    }
    // ── Polymorphic CRUD helpers ─────────────────────────────────────────
    async deleteEntityByType(entityType, id) {
        const plural = LimsApiClient.ENTITY_TYPE_TO_PLURAL[entityType];
        if (!plural)
            throw new Error(`Unknown entity type: ${entityType}`);
        return this.deleteEntity(plural, id);
    }
    async createEntityByType(entityType, attributes = {}) {
        const plural = LimsApiClient.ENTITY_TYPE_TO_PLURAL[entityType];
        if (!plural)
            throw new Error(`Unknown entity type: ${entityType}`);
        return this.request({
            method: 'POST',
            url: `/api/v0/lims/${plural}`,
            data: { entities: [{ attributes }] },
        });
    }
    async updateEntityByType(entityType, id, attributes) {
        const plural = LimsApiClient.ENTITY_TYPE_TO_PLURAL[entityType];
        if (!plural)
            throw new Error(`Unknown entity type: ${entityType}`);
        return this.request({
            method: 'PATCH',
            url: `/api/v0/lims/${plural}`,
            data: { entities: [{ id, attributes }] },
        });
    }
}
// ── Polymorphic entity access ────────────────────────────────────────
LimsApiClient.ENTITY_TYPE_TO_PLURAL = {
    sample: 'samples',
    test: 'tests',
    assay: 'assays',
    order: 'orders',
    attachment: 'attachments',
    lab: 'labs',
};
export function createLimsClient(config) {
    return new LimsApiClient(config);
}
